import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useProgress } from '../context/ProgressContext';
import { Confetti, ToastContainer } from '../components';
import type { ToastData } from '../components';
import './GamePage.css';

interface Coin {
  x: number;
  y: number;
  radius: number;
  collected: boolean;
}

interface Player {
  x: number;
  y: number;
  radius: number;
  speed: number;
}

type GameState = 'idle' | 'playing' | 'gameover';

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 400;
const GAME_DURATION = 30;
const COIN_COUNT = 5;
const PLAYER_SPEED = 5;
const PLAYER_RADIUS = 20;
const COIN_RADIUS = 15;
const TROPHY_THRESHOLD = 50;

export const GamePage: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const keysRef = useRef<Set<string>>(new Set());
  const animationRef = useRef<number>(0);

  const [gameState, setGameState] = useState<GameState>('idle');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [showConfetti, setShowConfetti] = useState(false);
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const { progress, updateGameHighScore, unlockTrophy } = useProgress();

  const playerRef = useRef<Player>({
    x: CANVAS_WIDTH / 2,
    y: CANVAS_HEIGHT / 2,
    radius: PLAYER_RADIUS,
    speed: PLAYER_SPEED,
  });

  const coinsRef = useRef<Coin[]>([]);

  const addToast = useCallback((message: string, type: ToastData['type']) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const generateCoin = useCallback((): Coin => {
    const margin = COIN_RADIUS * 2;
    return {
      x: margin + Math.random() * (CANVAS_WIDTH - margin * 2),
      y: margin + Math.random() * (CANVAS_HEIGHT - margin * 2),
      radius: COIN_RADIUS,
      collected: false,
    };
  }, []);

  const initGame = useCallback(() => {
    playerRef.current = {
      x: CANVAS_WIDTH / 2,
      y: CANVAS_HEIGHT / 2,
      radius: PLAYER_RADIUS,
      speed: PLAYER_SPEED,
    };

    coinsRef.current = Array.from({ length: COIN_COUNT }, generateCoin);
    setScore(0);
    setTimeLeft(GAME_DURATION);
  }, [generateCoin]);

  const checkCollision = (player: Player, coin: Coin): boolean => {
    const dx = player.x - coin.x;
    const dy = player.y - coin.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < player.radius + coin.radius;
  };

  const update = useCallback(() => {
    const player = playerRef.current;
    const keys = keysRef.current;

    // Movement
    if (keys.has('ArrowUp') || keys.has('w') || keys.has('W')) {
      player.y = Math.max(player.radius, player.y - player.speed);
    }
    if (keys.has('ArrowDown') || keys.has('s') || keys.has('S')) {
      player.y = Math.min(CANVAS_HEIGHT - player.radius, player.y + player.speed);
    }
    if (keys.has('ArrowLeft') || keys.has('a') || keys.has('A')) {
      player.x = Math.max(player.radius, player.x - player.speed);
    }
    if (keys.has('ArrowRight') || keys.has('d') || keys.has('D')) {
      player.x = Math.min(CANVAS_WIDTH - player.radius, player.x + player.speed);
    }

    // Check coin collisions
    coinsRef.current.forEach((coin, index) => {
      if (!coin.collected && checkCollision(player, coin)) {
        coin.collected = true;
        setScore((prev) => prev + 10);
        // Replace with new coin
        coinsRef.current[index] = generateCoin();
      }
    });
  }, [generateCoin]);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.lineWidth = 1;
    for (let x = 0; x < CANVAS_WIDTH; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, CANVAS_HEIGHT);
      ctx.stroke();
    }
    for (let y = 0; y < CANVAS_HEIGHT; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(CANVAS_WIDTH, y);
      ctx.stroke();
    }

    // Draw coins
    coinsRef.current.forEach((coin) => {
      if (!coin.collected) {
        // Outer glow
        ctx.beginPath();
        ctx.arc(coin.x, coin.y, coin.radius + 5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 215, 0, 0.2)';
        ctx.fill();

        // Coin
        ctx.beginPath();
        ctx.arc(coin.x, coin.y, coin.radius, 0, Math.PI * 2);
        const gradient = ctx.createRadialGradient(
          coin.x - 5,
          coin.y - 5,
          0,
          coin.x,
          coin.y,
          coin.radius
        );
        gradient.addColorStop(0, '#FFE066');
        gradient.addColorStop(1, '#FFD700');
        ctx.fillStyle = gradient;
        ctx.fill();

        // Shine
        ctx.beginPath();
        ctx.arc(coin.x - 4, coin.y - 4, 4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.fill();

        // $ symbol
        ctx.fillStyle = '#B8860B';
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('$', coin.x, coin.y);
      }
    });

    // Draw player
    const player = playerRef.current;

    // Player shadow
    ctx.beginPath();
    ctx.arc(player.x + 2, player.y + 2, player.radius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fill();

    // Player body
    ctx.beginPath();
    ctx.arc(player.x, player.y, player.radius, 0, Math.PI * 2);
    const playerGradient = ctx.createRadialGradient(
      player.x - 5,
      player.y - 5,
      0,
      player.x,
      player.y,
      player.radius
    );
    playerGradient.addColorStop(0, '#74b9ff');
    playerGradient.addColorStop(1, '#0984e3');
    ctx.fillStyle = playerGradient;
    ctx.fill();

    // Player eyes
    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(player.x - 6, player.y - 4, 5, 0, Math.PI * 2);
    ctx.arc(player.x + 6, player.y - 4, 5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.arc(player.x - 5, player.y - 4, 2, 0, Math.PI * 2);
    ctx.arc(player.x + 7, player.y - 4, 2, 0, Math.PI * 2);
    ctx.fill();

    // Player smile
    ctx.beginPath();
    ctx.arc(player.x, player.y + 2, 8, 0.2, Math.PI - 0.2);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, []);

  const gameLoop = useCallback(() => {
    if (gameState !== 'playing') return;

    update();
    render();
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [gameState, update, render]);

  // Game loop
  useEffect(() => {
    if (gameState === 'playing') {
      animationRef.current = requestAnimationFrame(gameLoop);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameState, gameLoop]);

  // Timer
  useEffect(() => {
    if (gameState !== 'playing') return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setGameState('gameover');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState]);

  // Game over handling
  useEffect(() => {
    if (gameState === 'gameover') {
      if (score > progress.gameHighScore) {
        updateGameHighScore(score);
        addToast('Nou High Score! 🎉', 'success');
      }

      if (score >= TROPHY_THRESHOLD && !progress.trophiesUnlocked['arcade-champ']) {
        unlockTrophy('arcade-champ');
        setShowConfetti(true);
        addToast('Ai câștigat: Arcade Champ! 🏆', 'success');
      }
    }
  }, [gameState, score, progress.gameHighScore, progress.trophiesUnlocked, updateGameHighScore, unlockTrophy, addToast]);

  // Keyboard handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
      }
      keysRef.current.add(e.key);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      keysRef.current.delete(e.key);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Initial render
  useEffect(() => {
    render();
  }, [render]);

  const startGame = () => {
    initGame();
    setGameState('playing');
  };

  const restartGame = () => {
    initGame();
    setGameState('playing');
  };

  return (
    <div className="game-page">
      <Confetti isActive={showConfetti} onComplete={() => setShowConfetti(false)} />
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <header className="game-header">
        <h1>🎮 Coin Collector</h1>
        <p className="game-subtitle">
          Colectează monede în 30 de secunde! Obține {TROPHY_THRESHOLD}+ puncte pentru trofeu.
        </p>
      </header>

      <div className="game-stats">
        <div className="stat">
          <span className="stat-label">Scor</span>
          <span className="stat-value">{score}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Timp</span>
          <span className={`stat-value ${timeLeft <= 10 ? 'warning' : ''}`}>
            {timeLeft}s
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">High Score</span>
          <span className="stat-value">{progress.gameHighScore}</span>
        </div>
      </div>

      <div className="game-container">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="game-canvas"
        />

        {gameState === 'idle' && (
          <div className="game-overlay">
            <div className="overlay-content">
              <h2>Coin Collector</h2>
              <p>Folosește WASD sau săgețile pentru a te mișca.</p>
              <p>Colectează monedele pentru puncte!</p>
              <button className="btn btn-primary btn-lg" onClick={startGame} type="button">
                Începe Jocul
              </button>
            </div>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="game-overlay">
            <div className="overlay-content">
              <h2>Game Over!</h2>
              <p className="final-score">Scor Final: {score}</p>
              {score >= TROPHY_THRESHOLD && (
                <p className="trophy-earned">🏆 Ai deblocat Arcade Champ!</p>
              )}
              {score > progress.gameHighScore && (
                <p className="new-record">🎉 Nou Record!</p>
              )}
              <button className="btn btn-primary btn-lg" onClick={restartGame} type="button">
                Joacă Din Nou
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="game-controls-info">
        <h3>Controale</h3>
        <div className="controls-grid">
          <div className="control-key">W / ↑</div>
          <div className="control-key">A / ←</div>
          <div className="control-key">S / ↓</div>
          <div className="control-key">D / →</div>
        </div>
      </div>
    </div>
  );
};
